import sanitizeHtml from "sanitize-html";
import { HeaderPositionType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class TableCategory extends BaseCategory {
    get name() {
        return "Table";
    }

    get type() {
        return SlideCategories.TABLE;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content includes data that can be represented as a table. Also use for pricing tables.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Provide a structured table with explicit column headers and body rows. Every body row must have the same number of cells as the headers, and every cell must contain a value.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            columnHeaders: ["array of column header strings"],
            bodyRows: [["array of row arrays, where each inner array has one string per column header"]],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        const normalized = this.normalizeTableModel(model);
        if (!normalized) {
            return false;
        }

        const expectedRowCount = normalized.rows.length;
        const expectedColCount = normalized.cols.length;
        const expectedCellCount = expectedRowCount * expectedColCount;

        if (normalized.cells.length !== expectedCellCount) {
            return false;
        }

        const seenCells = new Set();
        let hasPopulatedBodyCell = false;

        for (const cell of normalized.cells) {
            if (!Number.isInteger(cell?.row) || !Number.isInteger(cell?.col)) {
                return false;
            }

            if (cell.row < 0 || cell.row >= expectedRowCount || cell.col < 0 || cell.col >= expectedColCount) {
                return false;
            }

            const cellKey = `${cell.row}:${cell.col}`;
            if (seenCells.has(cellKey)) {
                return false;
            }
            seenCells.add(cellKey);

            const text = typeof cell?.text === "string" ? cell.text.trim() : "";
            if (!text) {
                return false;
            }

            if (cell.row > 0) {
                hasPopulatedBodyCell = true;
            }
        }

        return hasPopulatedBodyCell;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildTable(model, options)
        ];
        return models;
    }

    getRowStyle(style) {
        switch (style) {
            case "section":
                return "cleanRow";
            case "header":
                return "headerRow";
            case "summary":
                return "summaryRow";
            default:
                return "defaultRow";
        }
    }

    getColStyle(style) {
        switch (style) {
            case "header":
            case "category":
                return "headerCol";
            case "summary":
                return "summaryCol";
            case "emphasized":
                return "emphasizedCol";
            default:
                return "defaultCol";
        }
    }

    buildCell(model) {
        let value = sanitizeHtml(model.text, { allowedTags: [], allowedAttributes: {} });

        let cell = {
            row: model.row,
            col: model.col,
            cellText: { text: value },
            cellStyle: "text"
        };

        if (value.toLowerCase() == "yes" || value.toLowerCase() == "no" || value.toLowerCase() == "true" || value.toLowerCase() == "false") {
            cell.format = "icon";
            cell.content_type = "icon";
            cell.content_value = value.toLowerCase() == "yes" || value.toLowerCase() == "true" ? "check-yes" : "x";
            cell.cellColor = value.toLowerCase() == "yes" || value.toLowerCase() == "true" ? "positive" : "negative";
        }

        return cell;
    }

    normalizeTableModel(model) {
        if (this.validateArray(model?.columnHeaders) && this.validateArray(model?.bodyRows)) {
            const headers = model.columnHeaders.map(header => `${header ?? ""}`.trim());
            if (headers.some(header => !header)) {
                return null;
            }

            const bodyRows = model.bodyRows.map(row =>
                Array.isArray(row) ? row.map(value => `${value ?? ""}`.trim()) : null
            );

            if (bodyRows.some(row => !row || row.length !== headers.length || row.some(value => !value))) {
                return null;
            }

            return {
                rows: [
                    { row: 0, rowStyle: "header" },
                    ...bodyRows.map((_, index) => ({ row: index + 1, rowStyle: "plain" }))
                ],
                cols: headers.map((_, index) => ({ col: index, colStyle: "plain" })),
                cells: [
                    ...headers.map((text, col) => ({ row: 0, col, text, cellStyle: "header" })),
                    ...bodyRows.flatMap((row, rowIndex) =>
                        row.map((text, col) => ({ row: rowIndex + 1, col, text, cellStyle: "plain" }))
                    )
                ],
                conclusion: model.conclusion,
            };
        }

        if (!this.validateArray(model?.rows) || !this.validateArray(model?.cols) || !this.validateArray(model?.cells)) {
            return null;
        }

        return {
            rows: model.rows,
            cols: model.cols,
            cells: model.cells,
            conclusion: model.conclusion,
        };
    }

    buildTable(model, options = {}) {
        const normalized = this.normalizeTableModel(model);
        let builder = new SlideModelBuilder("dashboard", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let rows = normalized?.rows ?? [];
        let maxRowFromCells = (normalized?.cells ?? []).reduce((max, cell) => Math.max(max, cell.row), -1);
        if (maxRowFromCells + 1 > rows.length) {
            // Add missing rows
            for (let i = rows.length; i <= maxRowFromCells; i++) {
                rows.push({ row: i, rowStyle: "defaultRow" });
            }
        }

        let cols = normalized?.cols ?? [];
        let maxColFromCells = (normalized?.cells ?? []).reduce((max, cell) => Math.max(max, cell.col), -1);
        if (maxColFromCells + 1 > cols.length) {
            // Add missing cols
            for (let i = cols.length; i <= maxColFromCells; i++) {
                cols.push({ col: i, colStyle: "defaultCol" });
            }
        }

        builder.createPrimary({
            items: [{
                componentType: "Table",
                childElement: {
                    rows: rows.map((row, index) => ({
                        index,
                        style: this.getRowStyle(row.rowStyle),
                        size: row.rowStyle == "header" ? 20 : 50,
                    })),
                    cols: cols.map((col, index) => ({
                        index,
                        style: this.getColStyle(col.colStyle),
                    })),
                    cells: normalized.cells.map(cell => this.buildCell(cell)),
                }
            }],
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (normalized.conclusion) {
            builder.createBottomTray(normalized.conclusion);
        }

        return builder.model;
    }
}
